import {useState} from 'react';
import {
    Alert,
    Button,
    Grid,
} from '@mantine/core';
import {MdBugReport} from 'react-icons/md';
import {FiCheckCircle} from 'react-icons/fi';

import {
    FormPageLayout,
    PlatformLoader,
} from '@flexinet/ui-components';

import {Profile} from '../profile/Profile.tsx';
import {ContributingObservations} from '../components/ContributingObservations.tsx';
import {FileAsBugDrawer} from '../drawers/FileAsBugDrawer.tsx';
import {DetailPageNavigation} from '../navigation/DetailPageNavigation.tsx';
import {useDefectSignature} from '../hooks/useDefectSignature.ts';
import {DefectStatus} from '../types';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions.ts';
import {rbacActions} from '../../../config/rbac/actions.ts';

// FNP-904 — one defect, its evidence, and the two triage actions a human owns:
// acknowledge it, or file it. Everything else about the row belongs to the sweep.
export const DetailPage = () => {

    const [filingOpen, setFilingOpen] = useState(false);

    const {
        id,
        record,
        loading,
        isError,
        contributingObservations,
        observationsLoading,
        occurrenceCount,
        acknowledge,
        fileAs,
        updating,
        updateError,
    } = useDefectSignature();

    const {allowedActions} = useVerifiedPermissions();
    // Triage is a WRITE. Reading a defect and changing its state are separate
    // permissions — an operator who can see what is broken is not automatically
    // one who can declare it acknowledged or filed.
    const canTriage = allowedActions.includes(rbacActions.UpdateDefectSignature);

    if (loading) {
        return <PlatformLoader message='Loading defect...Please wait'/>;
    }

    // Never hang on the loader once the query has settled.
    if (isError || !record) {
        return (
            <Alert color='red' title='Defect not found'>
                No defect signature found for id <code>{id}</code>.
            </Alert>
        );
    }

    return (
        <FormPageLayout
            title={record.actionCode}
            pageNavigation={<DetailPageNavigation component={record}/>}
            tools={canTriage ? [
                <Button
                    key='acknowledge'
                    variant='outline'
                    leftSection={<FiCheckCircle/>}
                    loading={updating}
                    // Acknowledging an already-filed defect would walk its status
                    // backwards and lose the fact that a ticket exists.
                    disabled={record.status === DefectStatus.ACKNOWLEDGED
                        || record.status === DefectStatus.FILED}
                    onClick={acknowledge}
                >
                    Acknowledge
                </Button>,
                <Button
                    key='file'
                    leftSection={<MdBugReport/>}
                    onClick={() => setFilingOpen(true)}
                >
                    {record.jiraKey ? 'Update ticket link' : 'File as bug'}
                </Button>,
            ] : []}
        >
            {updateError && (
                <Alert color='red' title='Could not update this defect' mb='md'>
                    The triage change was not saved. Try again; if it persists the testing API
                    may be unavailable.
                </Alert>
            )}

            <Grid>
                <Grid.Col span={{
                    base: 12,
                    lg: 5
                }}>
                    <Profile record={record}/>
                </Grid.Col>
                <Grid.Col span={{
                    base: 12,
                    lg: 7
                }}>
                    <ContributingObservations
                        observations={contributingObservations}
                        occurrenceCount={occurrenceCount}
                        loading={observationsLoading}
                    />
                </Grid.Col>
            </Grid>

            <FileAsBugDrawer
                record={record}
                opened={filingOpen}
                filing={updating}
                onFile={fileAs}
                onClose={() => setFilingOpen(false)}
            />
        </FormPageLayout>
    );
};
